'use client'

import { cn } from '@/lib/utils'
import { EASE, Eyebrow, HAIRLINE, useReveal } from '@/components/page-kit'

import {
  DeliversHeading,
  useSolution,
  type Solution,
  type SolutionSlug,
} from './solution-kit'

/* AI's device: THE LOOP.

   The authored four steps close on themselves — "Continuous Learning: models
   evolve with each academic cycle" feeds straight back into "Data Ingestion".
   So the pipeline is drawn as a closed circuit with a return path rather than
   the open left-to-right track HRMS uses, and the caption on the return is the
   authored words from step 04.

   Distinct from Payroll's cycle, which is a *statement* that says "Every single
   cycle" in type: this one is actually drawn as a returning path. */

export function AiLoop({ slug }: { slug: SolutionSlug }) {
  const solution = useSolution(slug)
  const loop = useReveal<HTMLDivElement>(0.12)
  const steps = solution.howItWorks
  const last = steps[steps.length - 1]

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <Eyebrow>How it works</Eyebrow>

        <div ref={loop.ref} className="mt-8 sm:mt-10">
          {/* ══ lg+ : four stages across, with a return path underneath ══ */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-4 gap-6">
              {steps.map((s, i) => (
                <Stage key={s.step} step={s} index={i} on={loop.shown} />
              ))}
            </div>

            {/* The return: down from the last stage, back along the bottom, up
                into the first. Drawn as one bordered box so the corners are
                true radii and the arms land exactly on the outer columns. */}
            <div className="relative mt-8 h-20">
              <div
                aria-hidden
                className={cn(
                  'absolute inset-x-[12.5%] top-0 h-full rounded-b-[28px] border-x border-b',
                  'border-emerald-600/40 dark:border-primary-active/30'
                )}
                style={{
                  opacity: loop.shown ? 1 : 0,
                  transition: `opacity 700ms ${EASE} 520ms`,
                }}
              />
              <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-center">
                <span
                  className={cn(
                    'rounded-full border bg-background px-5 py-2 text-center',
                    'border-emerald-600/30 dark:border-primary-active/25'
                  )}
                  style={{
                    opacity: loop.shown ? 1 : 0,
                    transition: `opacity 600ms ${EASE} 760ms`,
                  }}
                >
                  <span className="text-[11px] uppercase tracking-[0.14em] text-emerald-600 sm:text-xs dark:text-primary-active">
                    {last!.title}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* ══ < lg : the same loop, rotated ══ */}
          <div className="lg:hidden">
            <div className="relative pl-7">
              <span
                aria-hidden
                className="absolute bottom-8 left-[6px] top-6 w-px origin-top bg-emerald-600/40 dark:bg-primary-active/30"
                style={{
                  transform: loop.shown ? 'scaleY(1)' : 'scaleY(0)',
                  transition: `transform 900ms ${EASE} 120ms`,
                }}
              />

              <div className="space-y-4">
                {steps.map((s, i) => (
                  <div key={s.step} className="relative">
                    <span
                      aria-hidden
                      className="absolute left-[-27px] top-5 size-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-600/40 bg-background dark:border-primary-active/30"
                      style={{
                        opacity: loop.shown ? 1 : 0,
                        transition: `opacity 300ms ${EASE} ${240 + i * 80}ms`,
                      }}
                    />
                    <Stage step={s} index={i} on={loop.shown} />
                  </div>
                ))}
              </div>

              {/* Return arm back to the top. */}
              <div
                aria-hidden
                className="absolute bottom-0 left-[6px] h-8 w-10 rounded-bl-[16px] border-b border-l border-emerald-600/40 dark:border-primary-active/30"
                style={{
                  opacity: loop.shown ? 1 : 0,
                  transition: `opacity 600ms ${EASE} 700ms`,
                }}
              />
            </div>

            <p className="mt-4 pl-12 text-[11px] uppercase tracking-[0.14em] text-emerald-600 dark:text-primary-active">
              {last!.title}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

function Stage({
  step,
  index,
  on,
}: {
  step: Solution['howItWorks'][number]
  index: number
  on: boolean
}) {
  return (
    <div
      className={cn('border-t pt-4', HAIRLINE)}
      style={{
        opacity: on ? 1 : 0,
        transform: on ? 'none' : 'translateY(10px)',
        transition: `opacity 520ms ${EASE} ${index * 100}ms, transform 520ms ${EASE} ${index * 100}ms`,
      }}
    >
      <span className="font-heading text-sm font-normal tabular-nums text-emerald-600 dark:text-primary-active">
        {step.step}
      </span>
      <h3 className="font-heading mt-2 text-base font-normal leading-snug text-foreground sm:text-lg">
        {step.title}
      </h3>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
        {step.text}
      </p>
    </div>
  )
}

/* The six things the AI layer enables, as a 2×3 of quiet blocks — the loop
   above is this page's signature, so this section stays deliberately plain. */
export function AiEnables({ slug }: { slug: SolutionSlug }) {
  const solution = useSolution(slug)
  const grid = useReveal<HTMLDivElement>(0.1)

  return (
    <section id="delivers" className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <Eyebrow>The intelligence layer</Eyebrow>
        <DeliversHeading solution={solution} />

        <div ref={grid.ref} className="mt-10 sm:mt-12">
          <div className="grid grid-cols-1 gap-px bg-border/70 sm:grid-cols-2 lg:grid-cols-3">
            {solution.delivers.map((d, i) => {
              const Glyph = d.icon
              return (
                <div
                  key={d.title}
                  className="relative bg-background px-0 py-6 sm:px-6 sm:py-7"
                  style={{
                    opacity: grid.shown ? 1 : 0,
                    transform: grid.shown ? 'none' : 'translateY(10px)',
                    transition: `opacity 500ms ${EASE} ${i * 80}ms, transform 500ms ${EASE} ${i * 80}ms`,
                  }}
                >
                  <div className="flex items-baseline gap-3 pr-8">
                    <span className="text-[11px] font-semibold tabular-nums text-muted-foreground/60">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-heading text-base font-normal leading-snug text-foreground sm:text-lg">
                      {d.title}
                    </h3>
                  </div>
                  <p className="mt-2.5 pl-[26px] text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
                    {d.text}
                  </p>

                  <Glyph
                    aria-hidden
                    className="absolute right-0 top-6 size-4 text-foreground/25 sm:right-6 sm:top-7"
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
